import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { ChevronDown, Check, Calendar } from 'lucide-react';
import VietnameseDatePicker from '../documents/VietnameseDatePicker';

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Hôm nay' },
  { value: 'this_week', label: 'Tuần này' },
  { value: 'this_month', label: 'Tháng này' },
  { value: 'last_month', label: 'Tháng trước' },
  { value: 'this_quarter', label: 'Quý này' },
  { value: 'last_quarter', label: 'Quý trước' },
  { value: 'this_year', label: 'Năm nay' },
  { value: 'last_year', label: 'Năm trước' },
];

export default function DateRangeFilter({ dateRange, onDateRangeChange, onApplyFilter, errors = {} }) {
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const calculatePeriodRange = (periodKey) => {
    const now = dayjs();
    const currentMonth = now.month(); // 0-11
    const currentYear = now.year();
    const currentQuarter = Math.floor(currentMonth / 3);

    switch (periodKey) {
      case 'today':
        return {
          from: now.format('YYYY-MM-DD'),
          to: now.format('YYYY-MM-DD'),
        };
      case 'this_week': {
        const dayOfWeek = now.day(); // 0 (Sun) - 6 (Sat)
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        const monday = now.add(mondayOffset, 'day');
        const sunday = monday.add(6, 'day');
        return {
          from: monday.format('YYYY-MM-DD'),
          to: sunday.format('YYYY-MM-DD'),
        };
      }
      case 'this_month':
        return {
          from: now.startOf('month').format('YYYY-MM-DD'),
          to: now.endOf('month').format('YYYY-MM-DD'),
        };
      case 'last_month': {
        const lastMonth = now.subtract(1, 'month');
        return {
          from: lastMonth.startOf('month').format('YYYY-MM-DD'),
          to: lastMonth.endOf('month').format('YYYY-MM-DD'),
        };
      }
      case 'this_quarter': {
        const startMonth = currentQuarter * 3;
        return {
          from: now.month(startMonth).startOf('month').format('YYYY-MM-DD'),
          to: now.month(startMonth + 2).endOf('month').format('YYYY-MM-DD'),
        };
      }
      case 'last_quarter': {
        const prevQuarterYear = currentQuarter === 0 ? currentYear - 1 : currentYear;
        const prevQuarterIndex = currentQuarter === 0 ? 3 : currentQuarter - 1;
        const startMonth = prevQuarterIndex * 3;
        return {
          from: dayjs().year(prevQuarterYear).month(startMonth).startOf('month').format('YYYY-MM-DD'),
          to: dayjs().year(prevQuarterYear).month(startMonth + 2).endOf('month').format('YYYY-MM-DD'),
        };
      }
      case 'this_year':
        return {
          from: now.startOf('year').format('YYYY-MM-DD'),
          to: now.endOf('year').format('YYYY-MM-DD'),
        };
      case 'last_year': {
        const lastYear = now.subtract(1, 'year');
        return {
          from: lastYear.startOf('year').format('YYYY-MM-DD'),
          to: lastYear.endOf('year').format('YYYY-MM-DD'),
        };
      }
      default:
        return null;
    }
  };

  const handleSelectPeriod = (option) => {
    setSelectedPeriod(option.value);
    setIsDropdownOpen(false);
    const range = calculatePeriodRange(option.value);
    if (range) {
      onDateRangeChange(range);
    }
  };

  const currentLabel = PERIOD_OPTIONS.find((o) => o.value === selectedPeriod)?.label || '-- Chọn kỳ báo cáo --';

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 items-start gap-3 md:gap-4">
        {/* Từ ngày */}
        <div className="w-full flex flex-col">
          <label className="block text-sm font-medium text-gray-700 mb-1.5 truncate">
            Từ ngày
          </label>
          <VietnameseDatePicker
            value={dateRange.from}
            maxDate={dateRange.to || undefined}
            onChange={(isoVal) => {
              setSelectedPeriod('');
              onDateRangeChange({ ...dateRange, from: isoVal });
            }}
            error={!!errors.from}
            placeholder="dd/MM/yyyy"
            className="w-full"
          />
          {errors.from && (
            <p className="mt-1 text-xs text-red-600">{errors.from}</p>
          )}
        </div>

        {/* Đến ngày */}
        <div className="w-full flex flex-col">
          <label className="block text-sm font-medium text-gray-700 mb-1.5 truncate">
            Đến ngày
          </label>
          <VietnameseDatePicker
            value={dateRange.to}
            minDate={dateRange.from || undefined}
            onChange={(isoVal) => {
              setSelectedPeriod('');
              onDateRangeChange({ ...dateRange, to: isoVal });
            }}
            error={!!errors.to}
            placeholder="dd/MM/yyyy"
            className="w-full"
          />
          {errors.to && (
            <p className="mt-1 text-xs text-red-600">{errors.to}</p>
          )}
        </div>

        {/* Kỳ báo cáo - Custom Modern Dropdown */}
        <div className="w-full relative flex flex-col" ref={dropdownRef}>
          <label className="block text-sm font-medium text-gray-700 mb-1.5 truncate">
            Kỳ báo cáo
          </label>
          <button
            type="button"
            onClick={() => setIsDropdownOpen((prev) => !prev)}
            className={`w-full h-[38px] px-3 bg-white border rounded-lg text-sm flex items-center justify-between text-left transition-all cursor-pointer ${
              isDropdownOpen
                ? 'border-blue-500 ring-2 ring-blue-500/20 text-gray-900 bg-blue-50/20'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
            }`}
          >
            <span className={`truncate ${!selectedPeriod ? 'text-gray-500' : 'text-gray-900 font-medium'}`}>
              {currentLabel}
            </span>
            <ChevronDown
              className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${
                isDropdownOpen ? 'rotate-180 text-blue-600' : 'text-gray-400'
              }`}
            />
          </button>

          {/* Floating Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute z-50 mt-1 left-0 right-0 top-full bg-white rounded-xl shadow-xl border border-gray-200/80 p-1.5 max-h-64 overflow-y-auto animate-in fade-in zoom-in-95 duration-150">
              <div className="space-y-0.5">
                {PERIOD_OPTIONS.map((opt) => {
                  const isSelected = selectedPeriod === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handleSelectPeriod(opt)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between transition-colors cursor-pointer ${
                        isSelected
                          ? 'bg-blue-50 text-blue-600 font-semibold'
                          : 'text-gray-700 hover:bg-slate-100 hover:text-gray-900'
                      }`}
                    >
                      <span className="truncate">{opt.label}</span>
                      {isSelected && <Check className="w-4 h-4 text-blue-600 flex-shrink-0 ml-2" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Nút Áp dụng */}
        <div className="w-full flex flex-col">
          <label className="hidden sm:block text-sm font-medium text-transparent mb-1.5 select-none pointer-events-none">
            &nbsp;
          </label>
          <button
            type="button"
            onClick={onApplyFilter}
            className="w-full h-[38px] px-6 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 flex items-center justify-center shadow-xs cursor-pointer active:scale-[0.99]"
          >
            Áp dụng
          </button>
        </div>
      </div>
    </div>
  );
}
